import { useFamilies } from 'src/clients/hooks/useFamilies';
import { buildClientFamilyMap } from 'src/clients/hooks/useClientFamilyMap';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { getClientParticipantIds } from 'src/meetings/utils/event-utils';

/**
 * Resolves the family a meeting belongs to. A meeting has no family of its own,
 * so outside the family view it is the family the scoped client belongs to, or on
 * the hub the family of the first client attendee who has one. Client attendees
 * without a family (e.g. one just made from "Create client") are skipped so they
 * don't hide the household the rest of the meeting belongs to. Undefined when
 * there is none.
 */
export function useMeetingFamilyId(
  scope: MeetingScope,
  meeting: FamilyMeeting,
): { familyId: string | undefined; isLoading: boolean } {
  const { families, isLoading } = useFamilies();

  if (scope.kind === 'family') return { familyId: scope.familyId, isLoading: false };

  const familiesByClientId = buildClientFamilyMap(families);
  const clientIds =
    scope.kind === 'individual' ? [scope.clientId] : getClientParticipantIds(meeting);
  const familyId = clientIds
    .map(clientId => familiesByClientId.get(clientId)?.id)
    .find(id => id !== undefined);
  return { familyId, isLoading };
}
